import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback, useRef } from "react";
import {
  Card,
  Stack,
  Container,
  Typography,
  CircularProgress,
  Box,
  MenuItem,
  InputLabel,
  Select,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Alert,
  FormControl,
} from "@mui/material";
import Scrollbar from "../components/scrollbar";
import { useSearchParams } from "react-router-dom";
import CardsTable from "src/components/cards-table/CardsTable";
import { get, post } from "src/utils/networkUtils";
import { useSelector } from "react-redux";

export default function UserCardsPage() {
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [cards, setCards] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [season, setSeason] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchParams] = useSearchParams();
  const observer = useRef();

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addingCard, setAddingCard] = useState(false);
  const [error, setError] = useState("");
  const debounceTimer = useRef(null);

  const tenantsList = useSelector((state) => state.tenant.tenants);

  const LIMIT = 10;

  const lastCardRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  useEffect(() => {
    if (searchParams.has("user_id")) {
      const id = searchParams.get("user_id");
      setUserId(id);
    }
  }, [searchParams]);

  useEffect(() => {
    if (tenantsList && tenantsList.length > 0 && !tenant) {
      const defaultTenant = tenantsList.find((t) => t.isDefaultTenant === true);
      if (defaultTenant) {
        setTenant(defaultTenant.tenantId);
      } else {
        setTenant(tenantsList[0].tenantId);
      }
    }
  }, [tenantsList, tenant]);

  // Load the tenant's seasons and default to the current one
  useEffect(() => {
    if (!tenant) return;

    setSeasons([]);
    setSeason(null);

    get(`/admin/seasons?tenantId=${tenant}`)
      .then((response) => {
        const seasonsData = response.data || [];
        setSeasons(seasonsData);
        const currentSeason = seasonsData.find((s) => s.isCurrentSeason);
        if (currentSeason) {
          setSeason(currentSeason.seasonId);
        } else if (seasonsData.length > 0) {
          setSeason(seasonsData[0].seasonId);
        }
      })
      .catch((error) => {
        console.error("Error fetching seasons:", error);
      });
  }, [tenant]);

  // Reset cards when tenant or season changes
  useEffect(() => {
    if (tenant) {
      setPage(0);
      setCards([]);
      setHasMore(true);
    }
  }, [tenant, season]);

  useEffect(() => {
    if (!userId || !hasMore || !tenant || !season) return;

    setLoading(true);
    get(
      `/admin/users/${userId}/cards?offset=${page * LIMIT
      }&limit=${LIMIT}&tenantId=${tenant}&seasonId=${season}`
    )
      .then((response) => {
        if (page === 0) {
          setCards(response.cards || []);
        } else {
          setCards((prev) => [...prev, ...(response.cards || [])]);
        }

        if ((response.cards || []).length < LIMIT) {
          setHasMore(false);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching cards:", error);
        setLoading(false);
      });
  }, [userId, page, tenant, season, hasMore, refreshKey]);

  const refreshCards = useCallback(() => {
    setPage(0);
    setCards([]);
    setHasMore(true);
    setRefreshKey((prev) => prev + 1);
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setError("");

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Only search if query length >= 3
    if (value.length >= 3) {
      setSearchLoading(true);
      debounceTimer.current = setTimeout(() => {
        get(`/admin/scoreables/search?query=${encodeURIComponent(value)}&tenantId=${tenant}`)
          .then((response) => {
            setSearchResults(response.scoreables || []);
            setSearchLoading(false);
          })
          .catch((error) => {
            console.error("Error searching scoreables:", error);
            setError("Failed to search scoreables");
            setSearchLoading(false);
          });
      }, 500); // 500ms debounce
    } else {
      setSearchResults([]);
      setSearchLoading(false);
    }
  };

  const handleAddCard = async (scoreable) => {
    setAddingCard(true);
    setError("");

    try {
      await post("/admin/cards/add-to-user", {
        scoreableId: scoreable.scoreableId,
        userId: parseInt(userId),
        tenantId: tenant,
      });

      // Close modal and reset
      setModalOpen(false);
      setSearchQuery("");
      setSearchResults([]);

      // Refresh cards list
      refreshCards();
    } catch (error) {
      console.error("Error adding card:", error);
      setError(error.message || "Failed to add card");
    } finally {
      setAddingCard(false);
    }
  };

  const handleOpenModal = () => {
    setModalOpen(true);
    setSearchQuery("");
    setSearchResults([]);
    setError("");
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSearchQuery("");
    setSearchResults([]);
    setError("");
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
  };

  return (
    <>
      <Helmet>
        <title> User Cards </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            User Cards
          </Typography>

          <Stack direction="row" spacing={2} alignItems="flex-end">
            <Box sx={{ width: "200px" }}>
              <InputLabel>Tenant</InputLabel>
              <Select
                sx={{ width: "100%" }}
                value={tenant || ""}
                label="Tenant"
                onChange={(e) => {
                  setTenant(e.target.value);
                }}
              >
                {tenantsList && tenantsList.map((t) => (
                  <MenuItem key={t.tenantId} value={t.tenantId}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ width: "200px" }}>
              <InputLabel>Season</InputLabel>
              <FormControl sx={{ width: "100%" }}>
                <Select
                  value={seasons.length > 0 && season ? season : ""}
                  label="Season"
                  onChange={(e) => {
                    setSeason(e.target.value);
                  }}
                >
                  {seasons.map((s) => (
                    <MenuItem key={s.seasonId} value={s.seasonId}>
                      {s.displayName} {s.isCurrentSeason && "(Current)"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Button
              variant="contained"
              onClick={handleOpenModal}
              disabled={!userId || !tenant}
            >
              Add Card
            </Button>
          </Stack>
        </Stack>

        <Card>
          {cards.length === 0 && loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Scrollbar>
              <CardsTable
                cards={cards}
                userId={userId}
                lastCardRef={lastCardRef}
                refreshCards={refreshCards}
              />
              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </Scrollbar>
          )}
        </Card>

        {/* Add Card Modal */}
        <Dialog
          open={modalOpen}
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Card to User</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Search Scoreable"
              type="text"
              fullWidth
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Type at least 3 characters..."
              helperText="Search by first or last name"
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {searchLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {searchResults.length > 0 && (
              <List sx={{ mt: 2, maxHeight: 400, overflow: "auto" }}>
                {searchResults.map((scoreable) => (
                  <ListItem key={scoreable.scoreableId} disablePadding>
                    <ListItemButton
                      onClick={() => handleAddCard(scoreable)}
                      disabled={addingCard}
                    >
                      <ListItemText
                        primary={`${scoreable.firstname} ${scoreable.lastname}`}
                        secondary={`Rating: ${scoreable.rating || 'N/A'} | Team: ${scoreable.team || 'N/A'} | Position: ${scoreable.position || 'N/A'}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}

            {searchQuery.length >= 3 && !searchLoading && searchResults.length === 0 && (
              <Typography sx={{ mt: 2, textAlign: "center", color: "text.secondary" }}>
                No scoreables found
              </Typography>
            )}

            {searchQuery.length > 0 && searchQuery.length < 3 && (
              <Typography sx={{ mt: 2, textAlign: "center", color: "text.secondary" }}>
                Type at least 3 characters to search
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal} disabled={addingCard}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}
